import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  LayoutDashboard, 
  FileSearch, 
  AlertTriangle, 
  MessageSquare, 
  Settings, 
  Users, 
  LogOut,
  Menu,
  X,
  Shield,
  ChevronDown,
  Send,
  Plug,
  History,
  User,
  Eye,
  XCircle
} from 'lucide-react';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['admin', 'supervisor', 'verification_agent', 'fraud_analyst', 'auditor'] },
  { name: 'Case Queue', href: '/cases', icon: FileSearch, roles: ['admin', 'supervisor', 'verification_agent', 'fraud_analyst', 'auditor'] },
  { name: 'Invitations', href: '/invitations', icon: Send, roles: ['admin', 'supervisor', 'verification_agent'] },
  { name: 'Templates', href: '/templates', icon: MessageSquare, roles: ['admin', 'supervisor'] },
  { name: 'Users', href: '/users', icon: Users, roles: ['admin'] },
  { name: 'Audit Trail', href: '/audit', icon: History, roles: ['admin'] },
  { name: 'Settings', href: '/settings', icon: Settings, roles: ['admin'] },
  { name: 'Integrations', href: '/integrations', icon: Plug, roles: ['admin'] },
];

export function Layout({ children }) {
  const { user, logout, hasPermission, isImpersonating, impersonation, stopImpersonation } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleStopImpersonation = async () => {
    await stopImpersonation();
    navigate('/users');
  };

  const filteredNavigation = navigation.filter(item => 
    hasPermission(item.roles)
  );

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800',
      supervisor: 'bg-blue-100 text-blue-800',
      verification_agent: 'bg-emerald-100 text-emerald-800',
      fraud_analyst: 'bg-orange-100 text-orange-800',
      auditor: 'bg-slate-100 text-slate-800'
    };
    return colors[role] || 'bg-slate-100 text-slate-800';
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-amber-950 px-4 py-2">
          <div className="max-w-screen-2xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              <span className="font-medium text-sm">
                Viewing as <strong>{user?.name}</strong> ({user?.role?.replace('_', ' ')})
              </span>
              <span className="text-amber-800 text-sm">• Read-only mode - no changes can be made</span>
            </div>
            <Button 
              size="sm" 
              variant="outline"
              onClick={handleStopImpersonation}
              className="bg-white hover:bg-amber-100 text-amber-900 border-amber-600"
            >
              <XCircle className="h-4 w-4 mr-1" />
              Exit Impersonation
            </Button>
          </div>
        </div>
      )}
      
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 transform transition-transform duration-200 ease-in-out lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full",
        isImpersonating && "top-10"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex flex-col justify-center h-20 px-6 border-b border-slate-700">
            <img 
              src="/edomatch-logo.png" 
              alt="Edomatch" 
              className="h-7 w-auto max-w-[140px] object-contain"
            />
            <span className="text-xs text-slate-400 mt-1">Identity verification console</span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto scrollbar-thin">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href || 
                (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all",
                    isActive 
                      ? "bg-white/10 text-white" 
                      : "text-slate-300 hover:bg-white/5 hover:text-white"
                  )}
                >
                  <item.icon className="h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-700">
            <div className="flex items-center gap-3 px-3 py-2">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-slate-700 text-white text-sm">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <span className={cn(
                  "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-0.5",
                  getRoleBadgeColor(user?.role)
                )}>
                  {user?.role?.replace('_', ' ')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={cn("lg:pl-64", isImpersonating && "pt-10")}>
        {/* Top header */}
        <header className={cn(
          "sticky z-30 flex items-center justify-between h-16 px-4 sm:px-6 bg-white border-b border-border",
          isImpersonating ? "top-10" : "top-0"
        )}>
          <button
            type="button"
            className="lg:hidden p-2 -ml-2 text-muted-foreground hover:text-foreground"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex-1 lg:flex-none" />

          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-slate-100 text-slate-700 text-sm">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-medium">{user?.name}</span>
                    <span className="text-xs text-muted-foreground font-normal">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to="/profile" className="flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    My Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="min-h-[calc(100vh-4rem)]">
          {children}
        </main>
      </div>
    </div>
  );
}

export default Layout;
